/**
 * AI Service - Frontend API client for Flow2
 *
 * Provides methods for generating and refining AI presentations.
 */

import { PresentationContent } from '../../../ai/schemas/ContentBlock';
import { MMOCommand, ScreenContext } from '../commands';

/**
 * Presentation command result from AI API
 */
export interface AIPresentationCommandResult {
  success: boolean;
  command?: MMOCommand;
  commands?: MMOCommand[];
  editMode?: 'content' | 'presentation';
  error?: string;
}

/**
 * Generation options for AI presentation
 */
export interface AIGenerationOptions {
  slideCount?: number;
  tone?: 'professional' | 'casual' | 'formal' | 'creative' | 'educational';
  audience?: string;
  // Content density. 'concise' (mobile-first) is Flow2's default; the server
  // defaults to concise when this is omitted. See docs/CONCISE_DETAILED_SWITCH_SPEC.md.
  contentStyle?: 'concise' | 'detailed';
  theme?: string;
  additionalInstructions?: string;
  templateId?: string;
  linkUrl?: string;
  // OpenRouter model id when the user picked something other than the
  // default Claude path from the optional model dropdown. Empty/undefined
  // means use the server default.
  model?: string;
}

/**
 * Generation result from AI API
 */
export interface AIGenerationResult {
  success: boolean;
  presentation?: PresentationContent;
  sessionId?: string;
  error?: string;
  creditsUsed?: number;
  creditsRemaining?: number;
  balance?: number; // For insufficient credits error
  required?: number; // For insufficient credits error
}

/**
 * Result of the async create flow — the worker has already compiled +
 * persisted the flow server-side, so all the client needs is the string_id.
 */
export interface AICreateAsyncResult {
  success: boolean;
  stringId?: string;
  editUrl?: string;
  url?: string;
  title?: string;
  error?: string;
}

/**
 * Job status shape (subset) returned by /api/job-status/{id}.
 */
export interface AIJobStatus {
  status: 'pending' | 'processing' | 'complete' | 'failed' | string;
  step?: string;
  progress?: string;
  flow_id?: string;
  string_id?: string;
  edit_url?: string;
  url?: string;
  title?: string;
  error?: string;
}

/**
 * Refinement result from AI API
 */
export interface AIRefinementResult {
  success: boolean;
  presentation?: PresentationContent;
  sessionId?: string;
  error?: string;
}

/**
 * History result from AI API
 */
export interface AIHistoryResult {
  success: boolean;
  history?: Array<{ role: string; content: string }>;
  presentation?: PresentationContent;
  error?: string;
}

/**
 * Credits info from AI API
 */
export interface AICreditsInfo {
  success: boolean;
  balance: number;
  plan: string;
  is_admin?: boolean;
  lifetime_earned: number;
  lifetime_spent: number;
  transactions: Array<{
    id: number;
    amount: number;
    type: string;
    description: string;
    reference_id?: string;
    created_at: string;
  }>;
  costs: {
    generation: number;
    refinement: number;
    image: number;
    signup_bonus: number;
  };
  error?: string;
}

/**
 * Available AI resources
 */
export interface AIResources {
  layouts: string[];
  themes: string[];
  screenTypes: string[];
}

/**
 * Export result from AI API
 */
export interface AIExportResult {
  success: boolean;
  stringId?: string;
  editUrl?: string;
  viewUrl?: string;
  error?: string;
}

/**
 * Turn a non-OK fetch Response into a user-friendly error message. Tries
 * hard to pull a useful string from the response body (JSON `error` field,
 * JSON `message`, or first 200 chars of text); falls back to a plain-English
 * description of the status code. Never throws — safe to call in catch paths.
 *
 * @param response The failing fetch Response
 * @param action  Short noun for the attempted action — used in fallback
 *                messages ("upload", "generate", "refine", etc.)
 */
async function humanizeHttpError(response: Response, action: string = 'request'): Promise<string> {
  const status = response.status;

  // Try to parse a useful error from the body
  let bodyMsg = '';
  try {
    const ct = response.headers.get('content-type') || '';
    if (ct.includes('application/json')) {
      const data = await response.clone().json();
      bodyMsg = data?.error || data?.message || '';
      // Credit errors from the backend have a friendly message already
      if (data?.error === 'insufficient_credits') {
        return data.message || 'Not enough credits. Please top up to continue.';
      }
    } else {
      const text = await response.clone().text();
      if (text && text.length < 500) bodyMsg = text.trim();
    }
  } catch { /* ignore body-parse failures */ }

  if (bodyMsg) return bodyMsg;

  // Status-code fallbacks — human readable, with a specific hint per family
  if (status === 401) return 'Please sign in again to continue.';
  if (status === 402) return 'You\'re out of credits. Top up to continue.';
  if (status === 403) return `You don't have permission to ${action}.`;
  if (status === 404) {
    return `The ${action} endpoint isn\'t available. This is likely a deploy issue. Please try again in a moment, or contact support if it keeps happening.`;
  }
  if (status === 413) return 'That file is too large. Try a smaller one (under 25 MB).';
  if (status === 415) return 'That file type isn\'t supported.';
  if (status === 429) return 'Too many requests. Please wait a moment and try again.';
  if (status >= 500 && status < 600) {
    return `Our ${action} service hit an error (${status}). We\'ve logged it; please try again shortly.`;
  }

  return `Couldn\'t ${action} (error ${status}). Please try again.`;
}

/**
 * AI Service class
 */
class AIService {
  private baseUrl: string;

  constructor(baseUrl: string = '/ai') {
    this.baseUrl = baseUrl;
  }

  /**
   * Generate a new presentation from a prompt
   */
  async generate(
    prompt: string,
    options: AIGenerationOptions = {}
  ): Promise<AIGenerationResult> {
    try {
      const response = await fetch(`${this.baseUrl}/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          prompt,
          slide_count: options.slideCount,
          tone: options.tone,
          audience: options.audience,
          contentStyle: options.contentStyle,
          link_url: options.linkUrl || '',
          theme: options.theme,
          additional_instructions: options.additionalInstructions,
          template_id: options.templateId,
          model: options.model || '',
        }),
      });

      const data = await response.json();

      if (data.error) {
        return {
          success: false,
          error: data.error,
        };
      }

      return {
        success: true,
        presentation: data.presentation,
        sessionId: data.session_id,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  /**
   * Generate a presentation with streaming response
   *
   * Uses Server-Sent Events (SSE) to receive tokens as they arrive,
   * enabling progressive rendering of the presentation.
   *
   * @param prompt - The user's prompt
   * @param options - Generation options
   * @param onChunk - Called for each text chunk received
   * @param onComplete - Called when generation is complete with full presentation
   * @param onError - Called if an error occurs
   * @returns AbortController to cancel the stream
   */
  generateStream(
    prompt: string,
    options: AIGenerationOptions = {},
    onChunk: (text: string, fullText: string) => void,
    onComplete: (result: AIGenerationResult) => void,
    onError: (error: string) => void
  ): AbortController {
    const controller = new AbortController();

    const startStream = async () => {
      try {
        const response = await fetch(`${this.baseUrl}/generate-stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            prompt,
            slide_count: options.slideCount,
            tone: options.tone,
            audience: options.audience,
            contentStyle: options.contentStyle,
            link_url: options.linkUrl || '',
            theme: options.theme,
            additional_instructions: options.additionalInstructions,
            template_id: options.templateId,
            model: options.model || '',
          }),
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(await humanizeHttpError(response, 'generate'));
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        const decoder = new TextDecoder();
        let buffer = '';
        let fullText = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE events (separated by \n\n)
          let eventEnd;
          while ((eventEnd = buffer.indexOf('\n\n')) !== -1) {
            const eventBlock = buffer.slice(0, eventEnd);
            buffer = buffer.slice(eventEnd + 2);

            // Parse the event
            let eventType = '';
            let eventData = '';

            for (const line of eventBlock.split('\n')) {
              if (line.startsWith('event: ')) {
                eventType = line.slice(7);
              } else if (line.startsWith('data: ')) {
                eventData = line.slice(6);
              }
            }

            if (!eventData) continue;

            try {
              const data = JSON.parse(eventData);

              if (eventType === 'chunk' && data.text) {
                fullText += data.text;
                onChunk(data.text, fullText);
              } else if (eventType === 'complete') {
                onComplete({
                  success: true,
                  presentation: data.presentation,
                  sessionId: data.session_id,
                });
                return;
              } else if (eventType === 'error') {
                onError(data.error || 'Generation failed');
                return;
              }
            } catch {
              // Skip malformed JSON
            }
          }
        }

        // If we get here without a complete event, something went wrong
        onError('Stream ended unexpectedly');
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          onError('Generation cancelled');
        } else {
          onError(error instanceof Error ? error.message : 'Stream failed');
        }
      }
    };

    startStream();
    return controller;
  }

  /**
   * Disconnect-proof create. POSTs /ai/create-async (enqueues a server-side
   * job + spawns a detached worker that generates AND persists the flow), then
   * opens the SSE relay at /ai/create-stream/{job_id} for the live typing
   * preview. The worker compiles + exports server-side, so the live preview is
   * DISPLAY-ONLY: if this SSE drops (tab backgrounds, network blip), the flow
   * still gets saved and the caller can recover by polling getJobStatus().
   *
   * onChunk: unchanged contract — receives (delta, fullText); feed fullText to
   *   extractLiveStreamState exactly like generateStream did.
   * onComplete: receives { stringId } once the worker has saved the flow.
   * onError: terminal failure.
   *
   * Returns the job_id (once enqueued) via onEnqueued so the caller can persist
   * it for reconnect, plus the AbortController for the relay SSE.
   */
  createAsync(
    prompt: string,
    options: AIGenerationOptions & { aspectRatio?: 'widescreen' | 'standard' } = {},
    onChunk: (text: string, fullText: string) => void,
    onComplete: (result: AICreateAsyncResult) => void,
    onError: (error: string) => void,
    onEnqueued?: (jobId: string) => void
  ): AbortController {
    const controller = new AbortController();

    const run = async () => {
      // 1) Enqueue the job.
      let jobId: string;
      try {
        const enqueueResp = await fetch(`${this.baseUrl}/create-async`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            prompt,
            slide_count: options.slideCount,
            tone: options.tone,
            audience: options.audience,
            contentStyle: options.contentStyle,
            theme: options.theme,
            aspect_ratio: options.aspectRatio || 'widescreen',
            link_url: options.linkUrl || '',
            model: options.model || '',
          }),
        });
        const enqueueData = await enqueueResp.json();
        if (!enqueueResp.ok || !enqueueData.success || !enqueueData.job_id) {
          if (enqueueData && enqueueData.error === 'insufficient_credits') {
            onError('insufficient_credits');
          } else {
            onError((enqueueData && (enqueueData.message || enqueueData.error)) || 'Failed to start generation');
          }
          return;
        }
        jobId = enqueueData.job_id;
      } catch (err) {
        onError(err instanceof Error ? err.message : 'Failed to start generation');
        return;
      }

      if (onEnqueued) onEnqueued(jobId);

      // 2) Open the relay SSE for the live typing preview + completion signal.
      this.streamJob(jobId, onChunk, onComplete, onError, controller.signal);
    };

    run();
    return controller;
  }

  /**
   * Open (or re-open) the SSE relay for a job and drive the typing preview +
   * completion. Used both by createAsync and by the reconnect path.
   */
  streamJob(
    jobId: string,
    onChunk: (text: string, fullText: string) => void,
    onComplete: (result: AICreateAsyncResult) => void,
    onError: (error: string) => void,
    signal?: AbortSignal
  ): void {
    const startStream = async () => {
      try {
        const response = await fetch(`${this.baseUrl}/create-stream/${encodeURIComponent(jobId)}`, {
          method: 'GET',
          signal,
        });
        if (!response.ok) {
          throw new Error(await humanizeHttpError(response, 'generate'));
        }
        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';
        let fullText = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          let eventEnd;
          while ((eventEnd = buffer.indexOf('\n\n')) !== -1) {
            const eventBlock = buffer.slice(0, eventEnd);
            buffer = buffer.slice(eventEnd + 2);

            let eventType = '';
            let eventData = '';
            for (const line of eventBlock.split('\n')) {
              if (line.startsWith('event: ')) {
                eventType = line.slice(7);
              } else if (line.startsWith('data: ')) {
                eventData = line.slice(6);
              }
            }
            if (!eventData) continue;

            try {
              const data = JSON.parse(eventData);
              if (eventType === 'chunk' && typeof data.text === 'string') {
                fullText += data.text;
                onChunk(data.text, fullText);
              } else if (eventType === 'complete') {
                onComplete({
                  success: true,
                  stringId: data.string_id,
                  editUrl: data.edit_url,
                  url: data.url,
                  title: data.title,
                });
                return;
              } else if (eventType === 'error') {
                onError(data.message || data.error || 'Generation failed');
                return;
              }
            } catch {
              // Skip malformed JSON
            }
          }
        }
        // Stream ended without a terminal event — the relay timed out or the
        // connection dropped. The caller's reconnect path (job-status poll)
        // resolves the real outcome; surface a soft signal here.
        onError('stream_incomplete');
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          // Aborted (unmount / user navigation) — not a real failure.
          return;
        }
        onError(error instanceof Error ? error.message : 'Stream failed');
      }
    };
    startStream();
  }

  /**
   * Poll a job's status (reconnect / fallback). Returns the parsed status row,
   * or null on a network error.
   */
  async getJobStatus(jobId: string): Promise<AIJobStatus | null> {
    try {
      const resp = await fetch(`/api/job-status/${encodeURIComponent(jobId)}`, { method: 'GET' });
      if (!resp.ok && resp.status !== 404) {
        // 404 is meaningful (job not found) — let the caller see it via status.
        const data = await resp.json().catch(() => null);
        if (data && data.status) return data as AIJobStatus;
        return null;
      }
      const data = await resp.json();
      return data as AIJobStatus;
    } catch {
      return null;
    }
  }

  /**
   * Bind a completed job's flow to the current browser session (POST
   * /ai/bind-job-flow). REQUIRED on the disconnect-recovery path: when the
   * relay SSE dropped and we resolve a `complete` job via the unauthenticated
   * /api/job-status poll, the anon edit-gate binding (_registerAnonFlow) that
   * normally happens inside the relay's `complete` branch never ran — so an
   * anon creator would be denied editing their own flow. This server round-trip
   * re-binds it. No-op server-side for signed-in users (still safe to call).
   * Returns the authoritative string_id on success, or null (caller falls back
   * to the polled string_id).
   */
  async bindJobFlow(jobId: string): Promise<string | null> {
    try {
      const resp = await fetch(`${this.baseUrl}/bind-job-flow`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ job_id: jobId }),
      });
      const data = await resp.json().catch(() => null);
      if (data && data.success && data.string_id) {
        return data.string_id as string;
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Import a deck (.pptx / .pdf) via the DETERMINISTIC converter — faithful
   * structure-preserving conversion, NOT the AI-reimagine upload path. Posts
   * the file to /ai/import-deck (multipart), then polls /api/job-status until
   * the background worker finishes and the flow is persisted.
   *
   * onProgress: receives the worker's current step string ("Converting deck",
   *   "Polishing screens", "Creating flow") for the progress UI.
   * onComplete: receives { editUrl, stringId, screens } once the flow is saved.
   * onError:   terminal failure (mapped error code or message).
   *
   * Returns an AbortController the caller can use to stop polling on unmount.
   */
  importDeck(
    file: File,
    onProgress: (step: string) => void,
    onComplete: (result: { editUrl?: string; stringId?: string; screens?: number }) => void,
    onError: (error: string) => void
  ): AbortController {
    const controller = new AbortController();

    const run = async () => {
      // 1) Upload the deck + enqueue the import job.
      let jobId: string;
      try {
        const form = new FormData();
        form.append('file', file);
        const resp = await fetch(`${this.baseUrl}/import-deck`, {
          method: 'POST',
          credentials: 'same-origin',
          body: form,
          signal: controller.signal,
        });
        const data = await resp.json().catch(() => null);
        if (!resp.ok || !data || !data.success || !data.job_id) {
          if (data && data.error === 'insufficient_credits') {
            onError('insufficient_credits');
          } else {
            onError((data && (data.message || data.error)) || await humanizeHttpError(resp, 'import'));
          }
          return;
        }
        jobId = data.job_id;
      } catch (err) {
        if (err instanceof Error && err.name === 'AbortError') return;
        onError(err instanceof Error ? err.message : 'Failed to start the import');
        return;
      }

      // 2) Poll job-status until complete/failed. Deck import has no SSE relay
      //    (the worker exits before the streaming path), so polling is the only
      //    completion signal. ~2s cadence, 10-minute safety ceiling (heavy
      //    60-page decks have run ~193s; typical 15-60s).
      const started = Date.now();
      const MAX_MS = 10 * 60 * 1000;
      const poll = async () => {
        if (controller.signal.aborted) return;
        if (Date.now() - started > MAX_MS) {
          onError('The import is taking longer than expected. Check My Flows in a moment.');
          return;
        }
        const st = await this.getJobStatus(jobId);
        if (controller.signal.aborted) return;
        if (st) {
          if (st.status === 'complete') {
            onComplete({
              editUrl: st.edit_url,
              stringId: st.flow_id || st.string_id,
              screens: (st as any).screens_created,
            });
            return;
          }
          if (st.status === 'failed') {
            onError(st.error || 'The import failed. Please try a different file.');
            return;
          }
          if (st.step || st.progress) onProgress(st.step || st.progress || '');
        }
        setTimeout(poll, 2000);
      };
      // First poll slightly delayed so the worker has a moment to spin up.
      setTimeout(poll, 1200);
    };

    run();
    return controller;
  }

  /**
   * Generate a presentation from an uploaded file (PDF, PPTX, TXT)
   */
  async upload(
    file: File,
    options: AIGenerationOptions = {},
    prompt?: string
  ): Promise<AIGenerationResult> {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (prompt) formData.append('prompt', prompt);
      if (options.slideCount) formData.append('slide_count', String(options.slideCount));
      if (options.tone) formData.append('tone', options.tone);
      if (options.contentStyle) formData.append('contentStyle', options.contentStyle);
      if (options.linkUrl) formData.append('link_url', options.linkUrl);
      if (options.theme) formData.append('theme', options.theme);
      if (options.model) formData.append('model', options.model);

      const response = await fetch(`${this.baseUrl}/upload`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (data.error) {
        // Handle insufficient credits error (402)
        if (data.error === 'insufficient_credits') {
          return {
            success: false,
            error: data.message || 'Not enough credits',
            balance: data.balance,
            required: data.required,
          };
        }
        return {
          success: false,
          error: data.error,
        };
      }

      return {
        success: true,
        presentation: data.presentation,
        sessionId: data.session_id,
        creditsUsed: data.credits_used,
        creditsRemaining: data.credits_remaining,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed',
      };
    }
  }

  /**
   * Generate a presentation from an uploaded file with SSE streaming
   * Provides real-time status updates: received -> extracting -> generating -> complete
   *
   * @param file - The uploaded file (PDF, PPTX, TXT)
   * @param options - Generation options
   * @param prompt - Optional user instructions
   * @param onStatus - Called for status updates (stage: received/extracting/generating)
   * @param onChunk - Called for each text chunk during generation
   * @param onComplete - Called when generation is complete
   * @param onError - Called if an error occurs
   * @returns AbortController to cancel the stream
   */
  uploadStream(
    file: File,
    options: AIGenerationOptions = {},
    prompt: string | undefined,
    onStatus: (stage: string, message: string) => void,
    onChunk: (text: string, fullText: string) => void,
    onComplete: (result: AIGenerationResult) => void,
    onError: (error: string) => void
  ): AbortController {
    const controller = new AbortController();

    const startStream = async () => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        if (prompt) formData.append('prompt', prompt);
        if (options.slideCount) formData.append('slide_count', String(options.slideCount));
        if (options.tone) formData.append('tone', options.tone);
      if (options.contentStyle) formData.append('contentStyle', options.contentStyle);
        if (options.linkUrl) formData.append('link_url', options.linkUrl);
        if (options.theme) formData.append('theme', options.theme);
        if (options.model) formData.append('model', options.model);

        const response = await fetch(`${this.baseUrl}/upload-stream`, {
          method: 'POST',
          body: formData,
          signal: controller.signal,
        });

        if (!response.ok) {
          throw new Error(await humanizeHttpError(response, 'upload'));
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        const decoder = new TextDecoder();
        let buffer = '';
        let fullText = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Parse SSE events (separated by \n\n)
          let eventEnd;
          while ((eventEnd = buffer.indexOf('\n\n')) !== -1) {
            const eventBlock = buffer.slice(0, eventEnd);
            buffer = buffer.slice(eventEnd + 2);

            // Parse the event
            let eventType = '';
            let eventData = '';

            for (const line of eventBlock.split('\n')) {
              if (line.startsWith('event: ')) {
                eventType = line.slice(7);
              } else if (line.startsWith('data: ')) {
                eventData = line.slice(6);
              }
            }

            if (!eventData) continue;

            try {
              const data = JSON.parse(eventData);

              if (eventType === 'status') {
                onStatus(data.stage, data.message);
              } else if (eventType === 'chunk' && data.text) {
                fullText += data.text;
                onChunk(data.text, fullText);
              } else if (eventType === 'complete') {
                onComplete({
                  success: true,
                  presentation: data.presentation,
                  sessionId: data.session_id,
                  creditsUsed: data.credits_used,
                  creditsRemaining: data.credits_remaining,
                });
                return;
              } else if (eventType === 'error') {
                onError(data.error || 'Generation failed');
                return;
              }
            } catch {
              // Skip malformed JSON
            }
          }
        }

        // If we get here without a complete event, something went wrong
        onError('Stream ended unexpectedly');
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          onError('Upload cancelled');
        } else {
          onError(error instanceof Error ? error.message : 'Upload failed');
        }
      }
    };

    startStream();
    return controller;
  }

  /**
   * Refine an existing presentation
   */
  async refine(
    sessionId: string,
    message: string,
    screenIndex?: number,
    signal?: AbortSignal
  ): Promise<AIRefinementResult> {
    try {
      const response = await fetch(`${this.baseUrl}/refine`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          message,
          screen_index: screenIndex,
        }),
        signal,
      });

      const data = await response.json();

      if (data.error) {
        return {
          success: false,
          error: data.error,
        };
      }

      return {
        success: true,
        presentation: data.presentation,
        sessionId: data.session_id,
      };
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return {
          success: false,
          error: 'Cancelled',
        };
      }
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  /**
   * Get conversation history
   */
  async getHistory(sessionId: string): Promise<AIHistoryResult> {
    try {
      const response = await fetch(`${this.baseUrl}/history/${sessionId}`);
      const data = await response.json();

      if (data.error) {
        return {
          success: false,
          error: data.error,
        };
      }

      return {
        success: true,
        history: data.history,
        presentation: data.presentation,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  /**
   * Get available resources (layouts, themes, etc.)
   */
  async getResources(): Promise<AIResources | null> {
    try {
      const response = await fetch(`${this.baseUrl}/resources`);
      const data = await response.json();

      if (!data.success) {
        return null;
      }

      return {
        layouts: data.layouts,
        themes: data.themes,
        screenTypes: data.screen_types,
      };
    } catch {
      return null;
    }
  }

  /**
   * Export presentation to a FlowVella flow
   *
   * @param sessionId - Session ID from generation
   * @param themeId - Theme ID to apply during compilation
   * @param title - Optional title override
   */
  async export(
    sessionId: string,
    themeId: string,
    title?: string
  ): Promise<AIExportResult> {
    try {
      const response = await fetch(`${this.baseUrl}/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          theme_id: themeId,
          title,
        }),
      });

      const data = await response.json();

      if (data.error) {
        return {
          success: false,
          error: data.error,
        };
      }

      return {
        success: true,
        stringId: data.string_id,
        editUrl: data.edit_url,
        viewUrl: data.view_url,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Export failed',
      };
    }
  }

  /**
   * Export a pre-compiled flow JSON (compiled on the frontend with full theme support)
   *
   * @param flowJson - Pre-compiled FlowVella document JSON
   * @param title - Flow title
   */
  async exportPrecompiled(
    flowJson: any,
    title: string
  ): Promise<AIExportResult> {
    try {
      const response = await fetch(`${this.baseUrl}/export`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          flow_json: flowJson,
          title,
        }),
      });

      const data = await response.json();

      if (data.error) {
        return {
          success: false,
          error: data.error,
        };
      }

      return {
        success: true,
        stringId: data.string_id,
        editUrl: data.edit_url,
        viewUrl: data.view_url,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Export failed',
      };
    }
  }

  /**
   * Get a presentation command from AI for direct MMO manipulation
   *
   * @param message - User's natural language command
   * @param screenContext - Context about the current screen and elements
   * @param extra - Additional options including abort signal
   */
  async getPresentationCommand(
    message: string,
    screenContext: ScreenContext,
    extra?: { richContext?: string; editHistory?: string; routeTo?: string; signal?: AbortSignal }
  ): Promise<AIPresentationCommandResult> {
    try {
      const response = await fetch(`${this.baseUrl}/presentation-command`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message,
          screen_context: screenContext,
          rich_context: extra?.richContext,
          edit_history: extra?.editHistory,
          route_to: extra?.routeTo,
        }),
        signal: extra?.signal,
      });

      const data = await response.json();

      if (data.error) {
        return {
          success: false,
          error: data.error,
        };
      }

      // Handle single command or batch
      if (data.commands && Array.isArray(data.commands)) {
        return {
          success: true,
          commands: data.commands,
          editMode: data.edit_mode || 'presentation',
        };
      }

      return {
        success: true,
        command: data.command,
        editMode: data.edit_mode || 'presentation',
      };
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        return {
          success: false,
          error: 'Cancelled',
        };
      }
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Command generation failed',
      };
    }
  }

  /**
   * Detect whether a message should use content or presentation editing
   *
   * @param message - User's message to classify
   */
  async detectEditMode(message: string): Promise<{ mode: 'content' | 'presentation'; confidence: number }> {
    try {
      const response = await fetch(`${this.baseUrl}/detect-edit-mode`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ message }),
      });

      const data = await response.json();

      return {
        mode: data.mode || 'content',
        confidence: data.confidence || 0.5,
      };
    } catch {
      // Default to content mode on error
      return { mode: 'content', confidence: 0.5 };
    }
  }

  /**
   * Generate an AI image for an image-placeholder block
   */
  async generateImage(
    prompt: string,
    blockId: string,
    style: string = 'photo',
    aspectRatio: string = '16:9'
  ): Promise<{ success: boolean; imageUrl?: string; blockId: string; error?: string }> {
    const sizeMap: Record<string, string> = {
      '16:9': '1792x1024',
      '4:3': '1024x1024',
      '1:1': '1024x1024',
      '3:4': '1024x1792',
      '9:16': '1024x1792',
    };
    const size = sizeMap[aspectRatio] || '1024x1024';

    try {
      const response = await fetch(`${this.baseUrl}/generate-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt, block_id: blockId, style, size }),
      });
      const data = await response.json();
      if (data.error) return { success: false, blockId, error: data.error };
      return { success: true, blockId, imageUrl: data.image_url };
    } catch (error) {
      return { success: false, blockId, error: error instanceof Error ? error.message : 'Failed' };
    }
  }

  /**
   * Search for a real-world image (books, logos, people, places, etc.)
   * Uses free APIs: Google Books, Clearbit, Wikimedia, Unsplash
   */
  async searchImage(
    query: string,
    blockId: string,
    searchType: 'book' | 'person' | 'logo' | 'place' | 'product' | 'general' = 'general'
  ): Promise<{
    success: boolean;
    imageUrl?: string;
    blockId: string;
    source?: string;
    sourceId?: string;
    photographerName?: string;
    photographerUrl?: string;
    attribution?: string;
    attributionHtml?: string;
    error?: string;
  }> {
    try {
      const response = await fetch(`${this.baseUrl}/search-image`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, block_id: blockId, type: searchType }),
      });
      const data = await response.json();
      if (data.error) return { success: false, blockId, error: data.error };
      return {
        success: true,
        blockId,
        imageUrl: data.image_url,
        source: data.source,
        sourceId: data.source_id,
        photographerName: data.photographer_name,
        photographerUrl: data.photographer_url,
        attribution: data.attribution,
        attributionHtml: data.attribution_html,
      };
    } catch (error) {
      return { success: false, blockId, error: error instanceof Error ? error.message : 'Search failed' };
    }
  }

  /**
   * Upload an image from a URL (for drag-drop from Google Images)
   */
  async uploadImageFromUrl(
    url: string,
    blockId: string
  ): Promise<{ success: boolean; imageUrl?: string; blockId: string; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/upload-image-url`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url, block_id: blockId }),
      });
      const data = await response.json();
      if (data.error) return { success: false, blockId, error: data.error };
      return { success: true, blockId, imageUrl: data.image_url };
    } catch (error) {
      return { success: false, blockId, error: error instanceof Error ? error.message : 'Failed' };
    }
  }

  /**
   * Upload a local image file (for drag-drop from desktop)
   */
  async uploadImageFile(
    file: File,
    blockId: string
  ): Promise<{ success: boolean; imageUrl?: string; blockId: string; error?: string }> {
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('block_id', blockId);
      const response = await fetch(`${this.baseUrl}/upload-image`, {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      if (data.error) return { success: false, blockId, error: data.error };
      return { success: true, blockId, imageUrl: data.image_url };
    } catch (error) {
      return { success: false, blockId, error: error instanceof Error ? error.message : 'Failed' };
    }
  }

  /**
   * Log feedback for unsupported/failed AI requests (fire-and-forget)
   */
  logFeedback(data: {
    request_text: string;
    request_type: 'unsupported' | 'llm_failure' | 'parse_failure' | 'command_failure';
    unsupported_reason?: string;
    error_detail?: string;
    screen_context?: any;
  }): void {
    fetch(`${this.baseUrl}/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data),
    }).catch(() => {});
  }

  /**
   * Get current user's credit balance and info
   */
  async getCredits(): Promise<AICreditsInfo> {
    try {
      const response = await fetch(`${this.baseUrl}/credits`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({ error: 'Failed to get credits' }));
        return {
          success: false,
          balance: 0,
          plan: 'free',
          lifetime_earned: 0,
          lifetime_spent: 0,
          transactions: [],
          costs: { generation: 10, refinement: 1, image: 2, signup_bonus: 50 },
          error: error.error || 'Failed to get credits',
        };
      }

      return await response.json();
    } catch (error) {
      return {
        success: false,
        balance: 0,
        plan: 'free',
        lifetime_earned: 0,
        lifetime_spent: 0,
        transactions: [],
        costs: { generation: 10, refinement: 1, image: 2, signup_bonus: 50 },
        error: error instanceof Error ? error.message : 'Failed to get credits',
      };
    }
  }

  /**
   * Get available credit packs for purchase
   */
  async getCreditPacks(): Promise<{ success: boolean; packs: Array<{ id: string; name: string; credits: number; price: string; price_cents: number }> }> {
    try {
      const response = await fetch(`${this.baseUrl}/credit-packs`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      return await response.json();
    } catch (error) {
      return { success: false, packs: [] };
    }
  }

  /**
   * Initiate a credit pack purchase - returns checkout URL
   */
  async purchaseCredits(pack: string): Promise<{ success: boolean; checkout_url?: string; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/purchase-credits`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pack }),
      });
      return await response.json();
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Purchase failed' };
    }
  }
}

// Export singleton instance
export const aiService = new AIService();

// Export class for testing
export { AIService };
